import React, { RefObject, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { AcceptanceFieldHint } from './AcceptanceFieldHint';
import {
  AcceptanceFieldRule,
  AcceptanceFieldBindingKind,
  AcceptanceManifest,
  AcceptanceRuntimeFieldCoverage,
} from './types';
import { requiredUnmountedProjectedFieldIds } from './runtimeFieldCoverage';

interface Props {
  manifest: AcceptanceManifest;
  documentKey?: string;
  anchorRef: RefObject<HTMLElement>;
  onCoverageChange?: (coverage: AcceptanceRuntimeFieldCoverage) => void;
  includeUnmodeledFields?: boolean;
}

export interface AcceptanceFieldTarget {
  field: AcceptanceFieldRule;
  target: HTMLElement;
  bindingKind: AcceptanceFieldBindingKind;
}

const selectorValue = (value: string): string => value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
const sameFieldId = (left: string | null | undefined, right: string | null | undefined): boolean => (
  !!left && !!right && left.trim().toLowerCase() === right.trim().toLowerCase()
);

const elementsWithFieldIdentity = (
  scope: HTMLElement,
  attribute: string,
  fieldId: string,
): HTMLElement[] => Array.from(scope.querySelectorAll<HTMLElement>(`[${attribute}]`))
  .filter(element => sameFieldId(element.getAttribute(attribute), fieldId));

const normalizedLabel = (value: string | null | undefined): string => (
  value || ''
).replace(/\s+/g, '').replace(/[：:*＊]+$/g, '').trim();

const nonBusinessFieldLabels = new Set(['操作', 'actions', 'action']);
const normalizedQueryLabel = (value: string | null | undefined): string => (
  normalizedLabel(value)
    .normalize('NFKC')
    .replace(/[.:：。．_-]/g, '')
    .replace(/(?:from|to|自|從|起始|開始|結束|截止|迄)$/i, '')
    .replace(/號$/g, '')
);

const labelCandidatesFor = (scope: HTMLElement): HTMLElement[] => (
  Array.from(scope.querySelectorAll<HTMLElement>(
    [
      'label',
      'th',
      '[role="columnheader"]',
      '.ant-descriptions-item-label',
      '.form-label',
      '.field-label',
      '.field-name',
      '.label-text',
    ].join(','),
  )).filter(candidate => (
    !candidate.closest('[data-acceptance-surface]')
    && !candidate.closest('[data-acceptance-non-field]')
    && normalizedLabel(candidate.textContent).length > 0
    && !nonBusinessFieldLabels.has(normalizedLabel(candidate.textContent).toLowerCase())
    && !candidate.matches('.ant-table-selection-column')
    && (
      !candidate.querySelector('input[type="radio"], input[type="checkbox"]')
      || candidate.hasAttribute('data-contract-field')
      || candidate.hasAttribute('data-field-id')
    )
  ))
);

const fieldScopeForAnchor = (anchor: HTMLElement): HTMLElement | null => {
  let scope = anchor.parentElement;
  while (
    scope
    && labelCandidatesFor(scope).length === 0
    && scope.parentElement
    && scope.parentElement !== document.body
  ) {
    scope = scope.parentElement;
  }
  return scope;
};

const labelForControl = (scope: HTMLElement, control: HTMLElement): HTMLElement | null => {
  if (control.matches(
    'label, th, [role="columnheader"], .ant-descriptions-item-label, .form-label, .field-label, .field-name, .label-text',
  )) return control;

  const tableHeader = control.closest<HTMLElement>('th, [role="columnheader"]');
  if (tableHeader && scope.contains(tableHeader)) return tableHeader;

  const id = control.getAttribute('id');
  if (id) {
    const explicit = scope.querySelector<HTMLElement>(`label[for="${selectorValue(id)}"]`);
    if (explicit) return explicit;
  }

  const formItem = control.closest<HTMLElement>('.ant-form-item');
  const antLabel = formItem?.querySelector<HTMLElement>('.ant-form-item-label label');
  if (antLabel) return antLabel;

  if (control.matches('input[type="radio"], input[type="checkbox"]')) {
    const wrappingLabel = control.closest<HTMLLabelElement>('label');
    const controlFieldId = control.getAttribute('data-contract-field')
      || control.getAttribute('data-field-id');
    const wrappingFieldId = wrappingLabel?.getAttribute('data-contract-field')
      || wrappingLabel?.getAttribute('data-field-id');
    if (
      wrappingLabel
      && scope.contains(wrappingLabel)
      && sameFieldId(controlFieldId, wrappingFieldId)
    ) return wrappingLabel;

    const group = control.closest<HTMLElement>('.radio-group, .checkbox-field, [role="radiogroup"]');
    let previous = group?.previousElementSibling;
    while (previous) {
      if (previous instanceof HTMLElement && previous.matches(
        'label, .form-label, .field-label, .field-name, .label-text',
      )) return previous;
      previous = previous.previousElementSibling;
    }
  }

  const tableCell = control.closest<HTMLTableCellElement>('td');
  if (tableCell?.parentElement) {
    const table = tableCell.closest('table');
    const header = table?.querySelectorAll<HTMLElement>('thead th')[tableCell.cellIndex];
    if (header) return header;
  }

  let branch: HTMLElement = control;
  for (let depth = 0; branch.parentElement && branch.parentElement !== scope && depth < 5; depth += 1) {
    let previous = branch.previousElementSibling;
    while (previous) {
      if (previous instanceof HTMLElement && previous.matches(
        'label, .ant-form-item-label, .form-label, .field-label, .field-name, .label-text',
      )) {
        if (previous.matches('.ant-form-item-label')) {
          return previous.querySelector<HTMLElement>('label');
        }
        return previous;
      }
      previous = previous.previousElementSibling;
    }
    branch = branch.parentElement;
  }

  return null;
};

export const findAcceptanceFieldTargets = (
  scope: HTMLElement,
  fields: AcceptanceFieldRule[],
): AcceptanceFieldTarget[] => {
  const claimedTargets = new Set<HTMLElement>();
  // Reserve every explicitly identified target before label inference begins.
  // Without this first pass, an earlier field with a similar label (for
  // example FD140_01 "入庫單號") can steal a later query field explicitly
  // marked as query_receipt_start ("入庫單號從").
  const explicitOwners = new Map<HTMLElement, string>();
  fields.forEach(field => {
    ['data-contract-field', 'data-field-id', 'name', 'id'].forEach(attribute => {
      elementsWithFieldIdentity(scope, attribute, field.fieldId).forEach(control => {
        const target = labelForControl(scope, control);
        if (target && !explicitOwners.has(target)) explicitOwners.set(target, field.fieldId);
      });
    });
  });
  const labelCandidates = labelCandidatesFor(scope);
  const labelCounts = new Map<string, number>();
  const queryLabelCounts = new Map<string, number>();
  fields.forEach(field => {
    const key = normalizedLabel(field.label);
    labelCounts.set(key, (labelCounts.get(key) || 0) + 1);
    const queryKey = normalizedQueryLabel(field.label);
    queryLabelCounts.set(queryKey, (queryLabelCounts.get(queryKey) || 0) + 1);
  });

  return fields.flatMap(field => {
    const fieldId = field.fieldId;
    const selectors: Array<[string, AcceptanceFieldBindingKind]> = [
      ['data-contract-field', 'data_contract_field'],
      ['data-field-id', 'data_field_id'],
      ['name', 'name'],
      ['id', 'id'],
    ];
    const controls = new Map<HTMLElement, AcceptanceFieldBindingKind>();
    for (const [attribute, kind] of selectors) {
      for (const control of elementsWithFieldIdentity(scope, attribute, fieldId)) {
        if (!controls.has(control)) controls.set(control, kind);
      }
    }

    const targets: AcceptanceFieldTarget[] = [];
    for (const [control, bindingKind] of Array.from(controls.entries())) {
      const target = labelForControl(scope, control);
      if (!target || claimedTargets.has(target)) continue;
      const existingHint = Array.from(
        target.querySelectorAll<HTMLElement>('[data-acceptance-hint-field]'),
      ).find(hint => sameFieldId(hint.getAttribute('data-acceptance-hint-field'), fieldId));
      claimedTargets.add(target);
      if (!existingHint || existingHint.closest('[data-acceptance-inline-generated]')) {
        targets.push({ field, target, bindingKind });
      }
    }

    const existingHint = Array.from(
      scope.querySelectorAll<HTMLElement>('[data-acceptance-hint-field]'),
    ).find(hint => sameFieldId(hint.getAttribute('data-acceptance-hint-field'), fieldId));
    const existingHintIsGenerated = !!existingHint?.closest('[data-acceptance-inline-generated]');
    if (targets.length === 0 && (!existingHint || existingHintIsGenerated)) {
      const expected = normalizedLabel(field.label);
      const queryExpected = normalizedQueryLabel(field.label);
      if (labelCounts.get(expected) === 1 || queryLabelCounts.get(queryExpected) === 1) {
        const target = labelCandidates.find(candidate => (
          !claimedTargets.has(candidate)
          && !explicitOwners.has(candidate)
          && (
            normalizedLabel(candidate.textContent) === expected
            || normalizedQueryLabel(candidate.textContent) === queryExpected
          )
        )) || null;
        if (target) {
          claimedTargets.add(target);
          targets.push({ field, target, bindingKind: 'unique_contract_label' });
        }
      }
    }

    return targets;
  });
};

const stableHash = (value: string): string => {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
};

const controlForLabel = (scope: HTMLElement, label: HTMLElement): HTMLElement | null => {
  const htmlFor = label.getAttribute('for');
  if (htmlFor) {
    const explicit = scope.querySelector<HTMLElement>(`#${selectorValue(htmlFor)}`);
    if (explicit) return explicit;
  }

  const formItem = label.closest<HTMLElement>('.ant-form-item');
  const antControl = formItem?.querySelector<HTMLElement>(
    '[data-contract-field], [data-field-id], input, select, textarea, [role="combobox"]',
  );
  if (antControl) return antControl;

  const sibling = label.nextElementSibling;
  if (sibling instanceof HTMLElement && sibling.matches(
    '[data-contract-field], [data-field-id], input, select, textarea, [role="combobox"]',
  )) return sibling;

  return null;
};

const fieldIdentityForLabel = (
  scope: HTMLElement,
  label: HTMLElement,
  formId: string,
  occurrence: number,
): { fieldId: string; table: string; column: string } => {
  const control = controlForLabel(scope, label);
  const explicit = control?.getAttribute('data-contract-field')
    || control?.getAttribute('data-field-id')
    || control?.getAttribute('name')
    || control?.getAttribute('id');
  const normalizedExplicit = explicit?.trim().toLowerCase();
  if (normalizedExplicit && /^[a-z][a-z0-9]*_\d+[a-z0-9]*$/i.test(normalizedExplicit)) {
    const column = normalizedExplicit.toUpperCase();
    return {
      fieldId: normalizedExplicit,
      table: column.split('_')[0],
      column,
    };
  }

  const labelText = normalizedLabel(label.textContent);
  return {
    fieldId: `ui_unmodeled_${stableHash(`${formId}|${labelText}|${occurrence}`)}`,
    table: 'UNMODELED_UI',
    column: 'UNMAPPED',
  };
};

const unmodeledFieldFor = (
  scope: HTMLElement,
  target: HTMLElement,
  manifest: AcceptanceManifest,
  occurrence: number,
): AcceptanceFieldRule => {
  const label = (target.textContent || '').replace(/\s+/g, ' ').trim();
  const identity = fieldIdentityForLabel(scope, target, manifest.formId, occurrence);
  return {
    ...identity,
    controlIds: [],
    label,
    description: '此欄位由驗收模式在實際畫面中發現，但目前 Contract 尚未建模。',
    technicalDescription: 'Runtime UI discovery found this visible field without a matching Contract field projection.',
    required: false,
    readOnly: target.matches('th, [role="columnheader"], .ant-descriptions-item-label'),
    allowedValues: [],
    validation: [],
    validationGuidance: [],
    evidenceStatus: 'manual_review',
    sourceStatus: 'runtime_ui_discovered',
    sourceRefs: [],
  };
};

export const scanAcceptanceUiFieldCoverage = (
  scope: HTMLElement,
  manifest: AcceptanceManifest,
  includeUnmodeledFields = true,
): { targets: AcceptanceFieldTarget[]; coverage: AcceptanceRuntimeFieldCoverage } => {
  const allCandidates = labelCandidatesFor(scope);
  const modeledTargets = findAcceptanceFieldTargets(scope, manifest.fields);
  const modeledByTarget = new Map(modeledTargets.map(item => [item.target, item.field]));
  const bindingByTarget = new Map(modeledTargets.map(item => [item.target, item.bindingKind]));
  const candidates = includeUnmodeledFields
    ? allCandidates
    : allCandidates.filter(candidate => {
      if (modeledByTarget.has(candidate)) return true;
      const existingFieldId = candidate
        .querySelector<HTMLElement>('[data-acceptance-hint-field]')
        ?.getAttribute('data-acceptance-hint-field');
      return manifest.fields.some(field => sameFieldId(field.fieldId, existingFieldId));
    });
  const modeledFields = new Map<string, AcceptanceFieldRule>();
  const unmodeledFields = new Map<string, AcceptanceFieldRule>();
  const targets: AcceptanceFieldTarget[] = [...modeledTargets];
  const occurrences = new Map<string, number>();

  for (const candidate of candidates) {
    const modeled = modeledByTarget.get(candidate);
    if (modeled) {
      modeledFields.set(modeled.fieldId, modeled);
      continue;
    }

    const existingModeled = Array.from(
      candidate.querySelectorAll<HTMLElement>('[data-acceptance-hint-field]'),
    ).flatMap(hint => {
      const existingFieldId = hint.getAttribute('data-acceptance-hint-field');
      const field = manifest.fields.find(item => sameFieldId(item.fieldId, existingFieldId));
      return field ? [field] : [];
    });
    if (existingModeled.length > 0) {
      existingModeled.forEach(field => modeledFields.set(field.fieldId, field));
      continue;
    }

    const key = normalizedLabel(candidate.textContent);
    const occurrence = occurrences.get(key) || 0;
    occurrences.set(key, occurrence + 1);
    const field = unmodeledFieldFor(scope, candidate, manifest, occurrence);
    unmodeledFields.set(field.fieldId, field);

    const generatedFieldId = candidate
      .querySelector<HTMLElement>('[data-acceptance-inline-generated]')
      ?.getAttribute('data-acceptance-inline-generated');
    if (
      !candidate.querySelector('[data-acceptance-hint-field]')
      || sameFieldId(generatedFieldId, field.fieldId)
    ) {
      targets.push({ field, target: candidate, bindingKind: 'unmodeled' });
    }
  }

  const mountedProjectedIds = new Set(modeledFields.keys());
  const projectedCounts = new Map<string, number>();
  manifest.fields.forEach(field => {
    projectedCounts.set(field.fieldId, (projectedCounts.get(field.fieldId) || 0) + 1);
  });
  const bindings = candidates.flatMap(candidate => {
    const modeled = modeledByTarget.get(candidate);
    if (modeled) {
      const label = (candidate.textContent || '').replace(/\s+/g, ' ').trim();
      const sameFieldBefore = candidates.slice(0, candidates.indexOf(candidate)).filter(previous => (
        modeledByTarget.get(previous)?.fieldId === modeled.fieldId
      )).length;
      return [{
        bindingId: `${manifest.formId}:${modeled.fieldId}:${stableHash(label)}:${sameFieldBefore}`,
        fieldId: modeled.fieldId,
        label,
        bindingKind: bindingByTarget.get(candidate) || 'unique_contract_label',
      }];
    }
    const label = (candidate.textContent || '').replace(/\s+/g, ' ').trim();
    const existingModeled = Array.from(
      candidate.querySelectorAll<HTMLElement>('[data-acceptance-hint-field]'),
    ).flatMap(hint => {
      const existingFieldId = hint.getAttribute('data-acceptance-hint-field');
      const field = manifest.fields.find(item => sameFieldId(item.fieldId, existingFieldId));
      return field ? [field] : [];
    });
    return existingModeled.map((field, hintIndex) => ({
      bindingId: `${manifest.formId}:${field.fieldId}:${stableHash(label)}:${hintIndex}`,
      fieldId: field.fieldId,
      label,
      bindingKind: 'manual_hint' as const,
    }));
  });
  return {
    targets,
    coverage: {
      candidateCount: candidates.length,
      modeledFields: Array.from(modeledFields.values()),
      unmodeledFields: Array.from(unmodeledFields.values()),
      unmountedProjectedFieldIds: requiredUnmountedProjectedFieldIds(
        manifest.fields,
        mountedProjectedIds,
      ),
      duplicateProjectedFieldIds: Array.from(projectedCounts.entries())
        .filter(([, count]) => count > 1)
        .map(([fieldId]) => fieldId),
      bindings,
    },
  };
};

const sameTargets = (left: AcceptanceFieldTarget[], right: AcceptanceFieldTarget[]): boolean => (
  left.length === right.length
  && left.every((item, index) => (
    item.field.fieldId === right[index].field.fieldId
    && item.target === right[index].target
  ))
);

export const AcceptanceInlineFieldHints: React.FC<Props> = ({
  manifest,
  documentKey,
  anchorRef,
  onCoverageChange,
  includeUnmodeledFields = true,
}) => {
  const [targets, setTargets] = useState<AcceptanceFieldTarget[]>([]);

  useEffect(() => {
    const anchor = anchorRef.current;
    const scope = anchor ? fieldScopeForAnchor(anchor) : null;
    if (!scope) return undefined;

    let frame: number | undefined;
    const refresh = () => {
      if (frame !== undefined) window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        const scan = scanAcceptanceUiFieldCoverage(scope, manifest, includeUnmodeledFields);
        const next = scan.targets;
        setTargets(current => sameTargets(current, next) ? current : next);
        onCoverageChange?.(scan.coverage);
      });
    };

    refresh();
    const observer = new MutationObserver(refresh);
    observer.observe(scope, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      if (frame !== undefined) window.cancelAnimationFrame(frame);
    };
  }, [anchorRef, includeUnmodeledFields, manifest, onCoverageChange]);

  return (
    <>
      {targets.map(({ field, target, bindingKind }) => createPortal(
        <span
          data-acceptance-inline-generated={field.fieldId}
          data-acceptance-binding-kind={bindingKind}
          data-acceptance-field-status={bindingKind === 'unmodeled' ? 'unmodeled' : 'modeled'}
        >
          <AcceptanceFieldHint
            manifest={manifest}
            fieldId={field.fieldId}
            documentKey={documentKey}
            fieldOverride={field}
          />
        </span>,
        target,
        `${manifest.formId}:${field.fieldId}`,
      ))}
    </>
  );
};
